import { useCallback, useEffect, useSyncExternalStore } from "react"
import { useUploadMonitor } from "@/contexts/uploadMonitorContext"
import type {
  GlobalUploadSnapshot,
  ProjectUploadSnapshot,
  UploadCompletion,
} from "@/lib/powersync/uploadMonitor"
import { keepAwake } from "@/platform"

/**
 * This project's upload status: its own queued rows, photos and tracks
 * (never another project's), what's ahead of them in the shared queue, and
 * its failures. Re-renders only when this project's snapshot changes.
 */
export function useProjectUploadStatus(
  projectId: string,
): ProjectUploadSnapshot {
  const monitor = useUploadMonitor()
  const getSnapshot = useCallback(
    () => monitor.getProjectSnapshot(projectId),
    [monitor, projectId],
  )
  return useSyncExternalStore(monitor.subscribe, getSnapshot)
}

/** Everything queued on the device, across all projects. */
export function useGlobalUploadStatus(): GlobalUploadSnapshot {
  const monitor = useUploadMonitor()
  const getSnapshot = useCallback(() => monitor.getGlobalSnapshot(), [monitor])
  return useSyncExternalStore(monitor.subscribe, getSnapshot)
}

/** Call `onCompleted` each time this project's upload queue empties. */
export function useUploadCompletions(
  projectId: string,
  onCompleted: (completion: UploadCompletion) => void,
): void {
  const monitor = useUploadMonitor()
  useEffect(
    () =>
      monitor.onProjectCompleted((completion) => {
        if (completion.projectId === projectId) onCompleted(completion)
      }),
    [monitor, projectId, onCompleted],
  )
}

/**
 * Hold the screen awake while a large upload is moving anywhere in the queue
 * (including another project's items that this project is waiting behind),
 * and let it sleep once the queue empties, stalls or goes offline. The OS
 * only honours the hold while the app is in the foreground.
 *
 * The effect synchronises an external system (the wake lock) with the
 * monitor's state, which is what effects are for.
 */
export function useUploadKeepAwake(): boolean {
  const { shouldKeepAwake } = useGlobalUploadStatus()
  useEffect(() => {
    if (!shouldKeepAwake) return
    keepAwake.hold().catch((error: unknown) => {
      console.warn("[KeepAwake] Could not keep the screen on:", error)
    })
    return () => {
      keepAwake.release().catch((error: unknown) => {
        console.warn("[KeepAwake] Could not release the screen:", error)
      })
    }
  }, [shouldKeepAwake])
  return shouldKeepAwake
}
